import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomSelect,
  CustomTextInput,
  Option,
  Text,
  useText
} from '@src/components';

import { cardTypeIcon } from '@src/utils/utils';
import { formCreditCardRegex } from '@src/utils/formRule';
import { monthsList, creditCardYears } from '@src/store/enums';

import { getBankOfBinNumber, setEditingCard } from '@src/store/slice/mobilExpressSlice';
import PropTypes from 'prop-types';
import '@src/styles/companyInformation/creditcards/newCreditCard.scss';

const NewCreditCards = ({ form }) => {
  const nameOnCard = useText('nameOnCard');
  const fourDigitCid = useText('fourDigitCid');
  const ccv3Digits = useText('ccv3Digits');
  const month = useText('month');
  const year = useText('year');
  const dispatch = useDispatch();

  const [cardTypeImage, setCardTypeImage] = useState(false);
  const [state, setState] = useState({
    cardNumber: form?.getFieldValue('cardNumber') || '',
    cardHolderName: form?.getFieldValue('cardHolderName') || '',
    expireMonthYear: form?.getFieldValue('expireMonthYear') || '',
    lastMonth: form?.getFieldValue('lastMonth') || null,
    lastYear: form?.getFieldValue('lastYear') || null,
    cvv: form?.getFieldValue('cvv') || '',
  });

  const stateAction = (prop, value) => {
    setState((state) => ({
      ...state,
      [prop]: value,
    }));
  };

  const creditCardNumberChange = useCallback(
    async (value) => {
      const cardNumber = value.replaceAll('_', '').replaceAll('-', '');
      let tempCard = cardNumber;
      if (tempCard.length > 6) {
        tempCard = tempCard.substring(0, 6);
      }
      if (value.length === 0) {
        setCardTypeImage(false);
      }

      // AMEX kartları için 15 haneli kontrol
      if (cardNumber.length === 15 && (tempCard.startsWith('34') || tempCard.startsWith('37'))) {
        const body = {
          bin: tempCard,
        };
        const action = await dispatch(getBankOfBinNumber(body));
        if (getBankOfBinNumber.fulfilled.match(action)) {
          setCardTypeImage(cardTypeIcon(action.payload.data?.cardType));
          return true;
        } else {
          setCardTypeImage(false);
        }
        return false;
      }

      // Diğer kartlar için 16 haneli kontrol
      if ((cardNumber.length === 16 || cardNumber.length === 6) && tempCard.length === 6) {
        const body = {
          bin: tempCard,
        };
        const action = await dispatch(getBankOfBinNumber(body));
        if (getBankOfBinNumber.fulfilled.match(action)) {
          setCardTypeImage(cardTypeIcon(action.payload.data?.cardType));
          return true;
        } else {
          setCardTypeImage(false);
        }
        return false;
      }
      return false;
    },
    [dispatch, form],
  );

  const isValidCardNumber = async (value) => {
    await creditCardNumberChange(value);
  };

  useEffect(() => {
    form?.setFieldsValue({
      cardNumber: state.cardNumber,
      cardHolderName: state.cardHolderName,
      lastMonth: state.lastMonth,
      lastYear: state.lastYear,
      cvv: state.cvv,
    });
    dispatch(setEditingCard({ creditCard: state }));
  }, [state]);

  useEffect(async () => {
    if (state.cardNumber.length === 0) {
      setCardTypeImage(false);
    } else {
      await creditCardNumberChange(state.cardNumber);
    }
  }, [state.cardNumber]);

  return (
    <CustomCollapseCard className="new-credit-card" cardTitle={<Text t="creditCardInformation" />}>
      <div className="new-credit-card-content">
        <CustomForm
          name="creditCardForm"
          className="credit-card-form"
          initialValues={{}}
          autoComplete="off"
          layout={'vertical'}
          form={form}
        >
          <div className="form-row">
            <CustomFormItem
              label={<Text t="nameOnCard" />}
              name="cardHolderName"
              rules={[{ required: true, message: <Text t="pleaseEnterNameOnCard" /> }]}
            >
              <CustomTextInput
                id="companyInformation-creditCards-newCreditCard-creditCardForm-cardHolderName-testInput"
                placeholder={nameOnCard}
                onInput={async (e) => {
                  stateAction('cardHolderName', e.target.value);
                }}
              />
            </CustomFormItem>
            <CustomFormItem
              label={<Text t="cardNumber2" />}
              name="cardNumber"
              className="card-number-form-item"
              rules={[
                { required: true, message: <Text t="pleaseEnterCardNumber" /> },
                {
                  validator: formCreditCardRegex,
                  message: <Text t="pleaseEnterAValidCardNumber" />,
                },
                {
                  validator: async (field, value) => {
                    try {
                      if (isValidCardNumber(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error());
                    } catch (e) {
                      return Promise.reject(new Error());
                    }
                  },
                  message: <Text t="pleaseEnterAValidCardNumber" />,
                },
              ]}
            >
              <CardInputMask
                id="companyInformation-creditCards-newCreditCard-creditCardForm-cardNumber-inputMask"
                value={state.cardNumber}
                cardTypeImage={cardTypeImage}
                creditCardNumberChange={async (value) => {
                  stateAction('cardNumber', value);
                }}
              />
            </CustomFormItem>
          </div>
          <div className="form-row">
            <CustomFormItem
              name={'expireMonthYear'}
              className="expire-form-item"
              label={
                <span>
                  <Text t="expirationDate" />
                  <span className="form-required-mark">*</span>
                </span>
              }
            >
              <div className="expire-form-item-content">
                <CustomFormItem
                  label={false}
                  name="lastMonth"
                  rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                >
                  <CustomSelect
                    id="companyInformation-creditCards-newCreditCard-creditCardForm-lastMonth-select"
                    placeholder={month}
                    onSelect={async (value) => {
                      stateAction('lastMonth', value);
                    }}
                  >
                    {monthsList.map((month) => (
                      <Option
                        id={`companyInformation-creditCards-newCreditCard-creditCardForm-lastMonth-${month.value}`}
                        key={month.value}
                        value={month.value}
                      >
                        {month.value}
                      </Option>
                    ))}
                  </CustomSelect>
                </CustomFormItem>

                <CustomFormItem
                  label={false}
                  name="lastYear"
                  rules={[{ required: true, message: 'Zorunlu Alan!' }]}
                >
                  <CustomSelect
                    id="companyInformation-creditCards-newCreditCard-creditCardForm-lastYear-select"
                    placeholder={year}
                    onSelect={async (value) => {
                      stateAction('lastYear', value);
                    }}
                  >
                    {creditCardYears.map((year) => (
                      <Option
                        id={`companyInformation-creditCards-newCreditCard-creditCardForm-lastYear-${year.value}`}
                        key={year.value}
                        value={year.value}
                      >
                        {year.value}
                      </Option>
                    ))}
                  </CustomSelect>
                </CustomFormItem>
              </div>
            </CustomFormItem>
            <CustomFormItem
              label={
                <span>
                  <Text t="securityCode" />
                  <span className="form-required-mark">*</span>
                </span>
              }
              name="cvv"
              className="cvv-form-item"
              rules={[
                {
                  validator: async (field, value) => {
                    const cleanedValue = value?.replace('_', '')?.replaceAll('-', '');
                    const cardNumber = form?.getFieldValue('cardNumber')?.replaceAll('-', '');

                    // AMEX kartları için 4 haneli, diğerleri için 3 haneli kontrol
                    if (
                      cardNumber?.startsWith('34') || cardNumber?.startsWith('37')
                        ? cleanedValue?.length === 4 // AMEX için 4 haneli
                        : cleanedValue?.length === 3 // Diğer kartlar için 3 haneli
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error());
                  },
                  message: <Text t="pleaseEnterAValidSecurityCode" />,
                },
              ]}
            >
              <CustomMaskInput
                id="companyInformation-creditCards-newCreditCard-creditCardForm-cvv-maskInput"
                mask={
                  form?.getFieldValue('cardNumber')?.startsWith('34') ||
                  form?.getFieldValue('cardNumber')?.startsWith('37')
                    ? '9999'
                    : '999'
                } // AMEX için 4 haneli, diğerleri için 3 haneli
                onChange={(e) => {
                  const value = e.target.value.replaceAll('_', '').replaceAll('-', '');
                  stateAction('cvv', value);
                }}
              >
                <CustomInput
                  id="companyInformation-creditCards-newCreditCard-creditCardForm-cvv-input"
                  placeholder={
                    form?.getFieldValue('cardNumber')?.startsWith('34') ||
                    form?.getFieldValue('cardNumber')?.startsWith('37')
                      ? fourDigitCid // AMEX için
                      : ccv3Digits // Diğer kartlar için
                  }
                />
              </CustomMaskInput>
            </CustomFormItem>
          </div>
          <div className="form-row">
            <div className="row info-text-contain">
              <div className="col-md-12">
                <span className="info-text-title">
                  <Text t="yourCardInformationWillBeStoredSecurelyIn" />
                </span>
              </div>
            </div>
          </div>
        </CustomForm>
      </div>
    </CustomCollapseCard>
  );
};

export default NewCreditCards;

const CardInputMask = ({ cardTypeImage, creditCardNumberChange, onChange, ...props }) => {
  const enterYour16DigitCardNumber = useText('enterYour16DigitCardNumber');
  const handleChange = (e) => {
    const value = e?.target?.value?.replaceAll('_', '')?.replaceAll('-', '');
    onChange?.(value);
    creditCardNumberChange?.(value);
  };
  const handlePaste = (event) => {
    const text = event.clipboardData.getData('text');
    const e = { target: { value: text } };
    handleChange(e);
  };

  // AMEX kartları için 15 haneli maske, diğerleri için 16 haneli maske
  const mask =
    props.value?.startsWith('34') || props.value?.startsWith('37')
      ? '9999-999999-99999'
      : '9999-9999-9999-9999';

  return (
    <CustomMaskInput
      {...props}
      id="companyInformation-creditCards-newCreditCard-creditCardForm-card-maskInput"
      mask={mask}
      onChange={handleChange}
      onPaste={handlePaste}
    >
      <CustomInput
        id="companyInformation-creditCards-newCreditCard-creditCardForm-card-input"
        placeholder={
          mask === '9999-999999-99999'
            ? '15 haneli kart numaranızı giriniz'
            : enterYour16DigitCardNumber
        }
        suffix={cardTypeImage ? <CustomImage src={cardTypeImage} /> : <span />}
      />
    </CustomMaskInput>
  );
};

NewCreditCards.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func,
    setFieldsValue: PropTypes.func,
  }),
};

CardInputMask.propTypes = {
  cardTypeImage: PropTypes.string,
  creditCardNumberChange: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.string,
};
